import type { CSSProperties, ReactNode } from "react";
import {
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useMemo,
  useState,
  useRef
} from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
  arrow as floatingArrow,
  FloatingArrow
} from "@floating-ui/react";
import type { Placement, UseDismissProps } from "@floating-ui/react";
import { cx } from "../../utils/classnames";

interface TooltipOptions {
  initialOpen?: boolean;
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  delay?: { open?: number; close?: number } | number;
  interactive?: boolean;
  dismissOptions?: UseDismissProps;
  showArrow?: boolean;
}

export function useTooltip({
  initialOpen = false,
  placement = "top",
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  delay,
  interactive = false,
  dismissOptions,
  showArrow = false
}: TooltipOptions = {}) {
  const arrowRef = useRef<SVGSVGElement | null>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const arrowHeight = 9;
  const arrowWidth = 10;
  const baseOffset = 5;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(baseOffset + (showArrow ? arrowHeight : 0)),
      flip({
        crossAxis: placement.includes("-"),
        fallbackAxisSideDirection: "start",
        padding: 5
      }),
      shift({ padding: 5 }),
      ...(showArrow ? [floatingArrow({ element: arrowRef, padding: 5 })] : [])
    ]
  });

  const context = data.context;
  const floatingContext = data.context;

  const hover = useHover(context, {
    move: false,
    enabled: !controlledOpen,
    delay,
    handleClose: interactive ? null : undefined
  });
  const focus = useFocus(context, {
    enabled: !controlledOpen
  });

  const dismiss = useDismiss(context, dismissOptions);
  const role = useRole(context, { role: "tooltip" });

  const interactions = useInteractions([hover, focus, dismiss, role]);

  return useMemo(
    () => ({
      open,
      setOpen,
      showArrow,
      arrowHeight,
      arrowWidth,
      arrowRef,
      floatingContext,
      ...interactions,
      ...data
    }),
    [
      open,
      setOpen,
      showArrow,
      arrowHeight,
      arrowWidth,
      arrowRef,
      floatingContext,
      interactions,
      data
    ]
  );
}

type ContextType =
  | (ReturnType<typeof useTooltip> & { floatingStyles?: CSSProperties })
  | null;

const TooltipContext = createContext<ContextType>(null);

export const useTooltipContext = () => {
  const context = useContext(TooltipContext);

  if (!context) {
    throw new Error("Tooltip components must be wrapped in <Tooltip />");
  }

  return context;
};

export function Tooltip({
  children,
  ...options
}: { children: ReactNode } & TooltipOptions) {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.

  const tooltip = useTooltip(options);
  return (
    <TooltipContext.Provider value={tooltip}>
      {children}
    </TooltipContext.Provider>
  );
}

export const TooltipTrigger = forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & { asChild?: boolean }
>(function TooltipTrigger({ children, asChild = false, ...props }, propRef) {
  const context = useTooltipContext();
  const childrenRef = (children as unknown as { ref: React.Ref<unknown> }).ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && isValidElement(children)) {
    return cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...(children.props as Record<string, unknown>),
        // @ts-expect-error -- this is a valid prop
        "data-state": context.open ? "open" : "closed"
      })
    );
  }

  return (
    <button
      // The user can style the trigger based on the state
      data-state={context.open ? "open" : "closed"}
      ref={ref as React.Ref<HTMLButtonElement>}
      type="button"
      {...context.getReferenceProps(props)}
    >
      {children}
    </button>
  );
});

export const TooltipContent = forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>(function TooltipContent({ style, ...props }, propRef) {
  const context = useTooltipContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  if (!context.open) return null;

  return (
    <FloatingPortal>
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        style={{
          ...context.floatingStyles,
          ...style
        }}
        {...context.getFloatingProps({
          ...(props as Record<string, unknown>),
          className: cx(
            "bg-alpha-800 backdrop-blur-sm shadow-tooltip",
            props.className
          )
        })}
      >
        {(props as { children?: ReactNode }).children}
        {context.showArrow ? (
          <FloatingArrow
            ref={context.arrowRef}
            context={context.floatingContext}
          />
        ) : null}
      </div>
    </FloatingPortal>
  );
});
